import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  memo,
} from "react";
import { Bell } from "lucide-react";
import {
  fetchNotifications,
  markNotificationViewed,
} from "../../api/notifications";
import {
  formatDistanceToNowStrict,
  format,
  isToday,
  isYesterday,
  parseISO,
} from "date-fns";
import { useNavigate } from "react-router-dom";
import { useMainCompany } from "../../context/MainCompanyContext";

const PAGE_SIZE = 10;

function notificationTimestamp(iso) {
  if (!iso) return "";
  let dt =
    typeof iso === "string"
      ? iso.includes("T")
        ? parseISO(iso)
        : new Date(iso)
      : new Date(iso);
  if (isNaN(dt.getTime())) dt = new Date();
  if (isToday(dt)) return `Today at ${format(dt, "h:mm aa")}`;
  if (isYesterday(dt)) return `Yesterday at ${format(dt, "h:mm aa")}`;
  const diff = Date.now() - dt.getTime();
  if (diff < 1000 * 60 * 60 * 24 * 7)
    return formatDistanceToNowStrict(dt, { addSuffix: true });
  return format(dt, "MMM d, yyyy h:mm aa");
}

const NotificationRow = memo(function NotificationRow({ n, onSelect }) {
  const ts =
    notificationTimestamp(n.created_at || n.createdAt || n.created) || "";
  return (
    <button
      type="button"
      onClick={() => onSelect(n)}
      className={`w-full text-left px-3 py-2 border-b hover:bg-gray-100 ${
        !n.viewed ? "bg-white font-medium" : "bg-transparent"
      }`}
      style={{ borderBottomColor: "rgb(229 231 235)" }}
    >
      <div className="text-sm text-gray-800">{n.notification_text}</div>
      <div className="text-xs text-gray-500">{ts}</div>
    </button>
  );
});

export default function NotificationsDropdown() {
  const { selectedMain } = useMainCompany();
  const [open, setOpen] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (selectedMain == null || selectedMain === "") return;
    setLoading(true);
    try {
      const data = await fetchNotifications(selectedMain);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      console.error("Failed loading notifications", e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [selectedMain]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open) setVisibleLimit(PAGE_SIZE);
  }, [open]);

  useEffect(() => {
    setVisibleLimit(PAGE_SIZE);
  }, [selectedMain]);

  useEffect(() => {
    function onDoc(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const onItemClick = useCallback(
    async (n) => {
      const extractUserId = (item) => {
        if (!item) return null;
        const d = item.data;
        return (
          item.userid ??
          item.user_id ??
          item.userId ??
          (d && typeof d === "object" ? d.user_id ?? d.userid ?? d.userId : null) ??
          (typeof d === "string"
            ? (() => {
                try {
                  const parsed = JSON.parse(d);
                  return parsed?.user_id ?? parsed?.userid ?? parsed?.userId;
                } catch {
                  return null;
                }
              })()
            : null)
        );
      };
      const userId = extractUserId(n);

      const params = new URLSearchParams();
      if (userId) params.set("userId", String(userId));
      const qs = params.toString();
      // Navigate first so a failing "mark viewed" call cannot trigger axios 401 → /login before routing.
      navigate(`/admin/users${qs ? `?${qs}` : ""}`);
      setOpen(false);

      try {
        await markNotificationViewed(n.id);
        setNotifications((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, viewed: true } : x))
        );
        setUnreadCount((c) => Math.max(0, c - (n.viewed ? 0 : 1)));
      } catch (e) {
        console.error("Failed marking viewed", e?.message || e);
      }
    },
    [navigate]
  );

  const visible = useMemo(
    () => notifications.slice(0, visibleLimit),
    [notifications, visibleLimit]
  );
  const totalCount = notifications.length;
  const canLoadMore = visibleLimit < totalCount;
  const canShowLess = visibleLimit > PAGE_SIZE;

  return (
    <div className="relative" ref={ref}>
      <button
        className="relative p-2 text-white hover:opacity-90"
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="menu"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full px-1">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 rounded shadow-lg border overflow-hidden z-50"
          style={{ backgroundColor: "rgb(249 250 251)" }}
        >
          <div
            className="p-2 border-b flex items-center justify-between"
            style={{ borderBottomColor: "rgb(229 231 235)" }}
          >
            <div className="font-semibold text-gray-800">Notifications</div>
            <div className="text-sm text-gray-600">{unreadCount} unread</div>
          </div>
          <div className="max-h-96 overflow-auto">
            {loading && (
              <div className="p-3 text-sm text-gray-600">Loading...</div>
            )}
            {!loading && visible.length === 0 && (
              <div className="p-3 text-sm text-gray-600">No notifications</div>
            )}
            {!loading &&
              visible.map((n) => (
                <NotificationRow key={n.id} n={n} onSelect={onItemClick} />
              ))}
          </div>
          <div
            className="p-2 border-t flex justify-between items-center"
            style={{ borderTopColor: "rgb(229 231 235)" }}
          >
            {canLoadMore ? (
              <button
                type="button"
                onClick={() =>
                  setVisibleLimit((c) =>
                    Math.min(c + PAGE_SIZE, totalCount)
                  )
                }
                className="text-sm text-blue-600"
              >
                Load more ({totalCount - visibleLimit} left)
              </button>
            ) : canShowLess ? (
              <button
                type="button"
                onClick={() => setVisibleLimit(PAGE_SIZE)}
                className="text-sm text-blue-600"
              >
                Show less
              </button>
            ) : (
              <div />
            )}
            <button onClick={load} className="text-sm text-gray-600">
              Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
